/**
 * alert-output-detection — shared helpers for classifying notebook cell
 * outputs rendered inside the alert canvas / modal.
 *
 * Dashboards can arrive in TWO shapes:
 *   1. A first-class output with `type === "dashboard"` (new path — see
 *      `components/tool-displays/add-replace-cell/success-new.tsx`, which
 *      detects `data-xynth-component="dashboard"` on raw HTML and promotes
 *      it to a dashboard output).
 *   2. A raw `type === "html"` output whose string contains the marker
 *      `data-xynth-component="dashboard"` — this happens when the output
 *      passes through a path that did NOT pre-normalize HTML (e.g. the
 *      alerts active-run-details endpoint, which streams raw cell outputs).
 *
 * Any component that cares about "is this a dashboard?" (tab ordering,
 * filmstrip hoisting, filtering charts/data tabs to exclude dashboards)
 * MUST use `isDashboardOutput()` so both shapes are handled consistently.
 */


export interface RawOutputLike {
  type?: string;
  data?: any;
}

/**
 * PKA emits `text/plain: "[Plotly Figure]"` alongside every Plotly chart
 * (see `persistent_kernel_api/v2/core/templates.py`). That placeholder
 * ends up in the cell's outputs as a normal `{type: "text"}` entry.
 * Every surface that shows stdout (node Stdout view, mini preview, modal
 * Print tab) must skip these or the user sees "[Plotly Figure]" as
 * "output" even though the actual Plotly chart already rendered
 * elsewhere on the card.
 *
 * Match is trimmed + case-insensitive. Empty strings also treated as
 * noise since an empty stdout entry is never useful.
 */
export function isNoiseStdoutText(s: unknown): boolean {
  if (typeof s !== "string") return true;
  const trimmed = s.trim();
  if (!trimmed) return true;
  if (/^\[plotly\s*figure\]$/i.test(trimmed)) return true;
  // IPython object reprs leak into text/plain when an IPython.display.* object
  // (e.g. HTML, Markdown, Image) is the last expression of a cell but its
  // rich `_repr_html_` was not captured on this surface. Showing the literal
  // "<IPython.core.display.HTML object>" as "output" is pure noise — the real
  // rendered content lives in the html/dashboard output, which is preferred.
  if (/^<IPython\.core\.display\.\w+ object>$/i.test(trimmed)) return true;
  return false;
}

/**
 * Return true if the given output is a dashboard — either by explicit
 * `type === "dashboard"` OR by HTML marker (`data-xynth-component="dashboard"`).
 * Whitespace-normalized + case-insensitive match on the marker.
 *
 * This ALSO returns true for `type === "dashboard_image"` so every
 * consumer that already hoists dashboards to the top (hero slot, tab
 * ordering, filmstrip filtering) keeps working without a per-site code
 * change. Consumers that need to render differently between HTML and
 * image should use `isDashboardImage()` below.
 */
export function isDashboardOutput(o: RawOutputLike | null | undefined): boolean {
  if (!o || typeof o !== "object") return false;
  if (o.type === "dashboard") return true;
  if (o.type === "dashboard_image") return true;
  if (o.type === "html" && typeof o.data === "string") {
    const normalized = o.data.replace(/\s+/g, " ").trim().toLowerCase();
    return normalized.includes('data-xynth-component="dashboard"');
  }
  return false;
}

/**
 * Return true if this is the NEW image-based dashboard output.
 *
 * Shape: `{ type: "dashboard_image", data: { url, width?, height?,
 * generated_at?, model? } }`.
 * AlertCanvas + AlertCellCarousel + AlertCellModal use this to switch
 * between `<DashboardView>` (iframe'd HTML) and `<DashboardImage>`
 * (single PNG). Returns false for legacy HTML dashboards — caller
 * should use `isDashboardOutput()` for "is this ANY dashboard shape".
 */
export function isDashboardImage(o: RawOutputLike | null | undefined): boolean {
  if (!o || typeof o !== "object") return false;
  if (o.type !== "dashboard_image") return false;
  const d = o.data;
  if (!d || typeof d !== "object") return false;
  return typeof d.url === "string" && d.url.length > 0;
}

/**
 * Normalize raw nbformat-style outputs (mime-keyed `data` dicts with NO
 * `type`) into the typed `{ type, data }` shape the canvas renderers expect.
 *
 * Why: AlertCanvas passes the cell's RAW outputs straight to the node (only
 * the modal normalizes). The trailing `auto_dashboard_render` cell stores its
 * dashboard as a raw `{ data: { "text/html": "<div data-xynth-component=
 * \"dashboard\">…", "text/plain": "<IPython…HTML object>" } }` output. Because
 * `type` is undefined and `data` is an object, `isDashboardOutput()` returned
 * false and the node fell back to showing the "<IPython…>" / stdout text
 * instead of the dashboard preview. Normalizing here makes detection AND
 * rendering work everywhere downstream (forceDashboardOnly, carousel,
 * MiniPreview) with no per-site change.
 *
 * Already-typed outputs are passed through untouched.
 */
export function normalizeAlertOutputs<T extends RawOutputLike>(
  outputs: T[] | undefined,
): T[] | undefined {
  if (!Array.isArray(outputs)) return outputs;
  return outputs.map((o) => {
    if (!o || typeof o !== "object") return o;
    if (typeof o.type === "string" && o.type) return o; // already normalized
    const data: any = (o as any).data;
    if (!data || typeof data !== "object") return o;

    const html = data["text/html"];
    if (typeof html === "string" && html) {
      const normalized = html.replace(/\s+/g, " ").trim().toLowerCase();
      if (normalized.includes('data-xynth-component="dashboard"')) {
        // Strip the outer wrapper so DashboardView doesn't double-wrap.
        const inner = html.match(
          /<div\s+data-xynth-component="dashboard">([\s\S]*)<\/div>\s*$/i,
        );
        return { ...o, type: "dashboard", data: inner ? inner[1] : html } as T;
      }
      return { ...o, type: "html", data: html } as T;
    }
    const png = data["image/png"];
    if (typeof png === "string" && png) {
      return { ...o, type: "image", data: png } as T;
    }
    const txt = data["text/plain"];
    if (typeof txt === "string") {
      return { ...o, type: "text", data: txt } as T;
    }
    return o;
  });
}

/**
 * Hoist dashboard outputs to index 0 without mutating the source array.
 * Relative order of all non-dashboard outputs is preserved.
 */
export function sortOutputsDashboardFirst<T extends RawOutputLike>(
  outputs: T[] | undefined,
): T[] | undefined {
  if (!Array.isArray(outputs) || outputs.length === 0) return outputs;
  const dashboard = outputs.filter((o) => isDashboardOutput(o));
  if (dashboard.length === 0) return outputs;
  const others = outputs.filter((o) => !isDashboardOutput(o));
  return [...dashboard, ...others];
}

/**
 * Rank an output for "visual impact". Lower rank = better visual = shown first.
 *
 * Order locked by user:  dashboard → unified_search → plotly → df → stdout
 *
 *   0  dashboard       — THE hero
 *   1  unified search  — news / twitter / reddit cards (rich carousel)
 *   2  plotly/bokeh/backtest-tabs — interactive agent-generated charts
 *   3  chart-like html — xynth-component charts (backtest-tabs html marker)
 *   4  image           — static PNG (loses to interactive on same cell)
 *   5  dataframe html  — tables, informative but not picture-grade
 *   6  agent results   — sub-agent / fund-agent carousels (rare in alerts)
 *   7  stdout / text   — prints are last-resort context
 *   8  error           — only shown if nothing else has useful content
 *   9  unknown         — never expected, safest to bury
 *
 * Used to (a) reorder the Chart tab so plotly leads / PNGs trail, and
 * (b) reorder the per-node rotation carousel so the FIRST slide is the
 * best visual (instead of whatever raw-notebook-order gave us).
 */
export function visualPriorityRank(o: RawOutputLike | null | undefined): number {
  if (!o || typeof o !== "object") return 9;
  const t = o.type;
  // Signal-registration cards are the hero output on a signal canvas.
  if (t === "signal_register") return 0;
  if (t === "automations") return 1;
  if (isDashboardOutput(o)) return 0;
  if (t === "unified_search") return 1;
  if (t === "plotly" || t === "bokeh" || t === "backtest-tabs") return 2;
  if (t === "html" && typeof o.data === "string") {
    const n = o.data.replace(/\s+/g, " ").trim().toLowerCase();
    if (
      n.includes('data-visualization-type="backtest-tabs"') &&
      n.includes('data-xynth-component="true"')
    ) {
      return 3;
    }
    if (/<table/i.test(o.data)) return 5;
    return 5;
  }
  if (t === "image") return 4;
  if (t === "fund_agent" || t === "sub_agent") return 6;
  if (t === "text") return 7;
  if (t === "error") return 8;
  return 9;
}

/**
 * Stable-sort outputs by visual priority. Same rank → original relative order
 * preserved. Returns a new array; does NOT mutate the input.
 */
export function sortOutputsByVisualPriority<T extends RawOutputLike>(
  outputs: T[] | undefined,
): T[] | undefined {
  if (!Array.isArray(outputs) || outputs.length === 0) return outputs;
  return outputs
    .map((o, i) => ({ o, i, rank: visualPriorityRank(o) }))
    .sort((a, b) => (a.rank === b.rank ? a.i - b.i : a.rank - b.rank))
    .map((x) => x.o);
}
